import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, AlertTriangle } from "lucide-react";
import { ARTIFACT_STEPS, scoresFrom } from "./artifactAdapter";

// Step 3 of the shared wizard for artifacts, in the slot where documents get
// "AI Temper Detection". There is no second request: the structural analysis
// already ran server-side inside /api/artifact/verify-file.
const SEV_TEXT = {
  critical: "text-red-600",
  high: "text-red-600",
  medium: "text-orange-500",
  low: "text-gray-500",
  info: "text-gray-400",
};

export default function ArtifactIntegrity() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentStep = 2;

  const uploadedFiles = location.state?.uploadedFiles || [];
  const verificationResult = location.state?.verificationResult || {};
  const findings = verificationResult.findings || [];
  const { aiScore, riskScore, classification } = scoresFrom(verificationResult);

  const clean = classification === "clean";
  const hashMatch = !!verificationResult.merkle_valid;

  const rows = [
    ["Binary integrity (hash match)", hashMatch],
    ["Signed digest matches artifact", hashMatch && !!verificationResult.pq_signature_valid],
    ["Structural comparison to upload baseline", findings.length === 0],
  ];

  const next = () =>
    navigate("/policy-key-validation", {
      state: { uploadedFiles, aiScore, riskScore, classification, verificationResult },
    });

  const back = () =>
    navigate("/automatic-verification", { state: { uploadedFiles, verificationResult } });

  let summaryMessage = "Some integrity checks need attention.";
  if (clean) {
    summaryMessage = "No unauthorized modification detected.";
  } else if (classification === "tampered") {
    summaryMessage = "This artifact does not match what was signed.";
  }

  return (
    <div className="w-full bg-white mx-auto">
      {/* STEPS */}
      <div className="flex items-center justify-between mb-2 h-[72px]">
        {ARTIFACT_STEPS.map((step, index) => {
          const isDone = index < currentStep;
          const isCurrent = index === currentStep;

          let circleClass = "bg-white border-gray-300 text-gray-500";
          let labelClass = "text-gray-500";
          if (isDone) {
            circleClass = "bg-green-500 border-green-500 text-white";
            labelClass = "text-green-600";
          } else if (isCurrent) {
            circleClass = "bg-blue-600 border-blue-600 text-white";
            labelClass = "text-blue-600";
          }

          return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== ARTIFACT_STEPS.length - 1 && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${index < currentStep ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${circleClass}`}>
              {index < currentStep ? <Check size={12} /> : index + 1}
            </div>
            <p className={`mt-1 text-sm font-medium text-center hidden sm:block ${labelClass}`}>{step}</p>
          </div>
          );
        })}
      </div>

      {/* SUMMARY */}
      <div className="text-center my-4">
        <h2 className="text-[32px] font-bold text-gray-800">{clean ? aiScore : riskScore}%</h2>
        <p className="text-sm text-gray-500">{clean ? "Integrity Score" : "Risk Score"}</p>
        <p className="text-16px text-gray-800 mt-1">{summaryMessage}</p>
        {verificationResult.format && (
          <p className="text-xs text-gray-400 mt-1">Detected format: {verificationResult.format}</p>
        )}
      </div>

      {/* CHECKS */}
      <div className="bg-gray-50 border rounded-2xl p-3 mb-4">
        <h3 className="text-20px font-semibold text-gray-700 mb-2">Integrity Checks</h3>
        <div className="space-y-2">
          {rows.map(([title, ok]) => (
            <div key={title} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white">
              <div className="flex items-center gap-3">
                {ok ? <CheckCircle2 size={18} className="text-green-500" /> : <div className="text-red-500 font-bold text-sm">✕</div>}
                <p className="text-gray-800 text-14px">{title}</p>
              </div>
              <span className={`font-semibold text-sm ${ok ? "text-green-600" : "text-red-600"}`}>{ok ? "Passed" : "Failed"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FINDINGS */}
      <div className="bg-gray-50 border rounded-2xl p-3 mb-4">
        <h3 className="text-20px font-semibold text-gray-700 mb-2">Modification Findings</h3>
        {findings.length === 0 ? (
          <p className="text-sm text-gray-400 px-1 py-2">No structural differences from the uploaded baseline.</p>
        ) : (
          <div className="space-y-2">
            {findings.map((f) => (
              <div key={f.finding_id} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2 bg-white gap-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={16} className={SEV_TEXT[f.severity] || "text-gray-500"} />
                  <p className="text-gray-800 text-14px">{f.description}</p>
                </div>
                <span className={`text-xs font-semibold flex-shrink-0 ${SEV_TEXT[f.severity] || "text-gray-500"}`}>
                  {String(f.severity).toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-2 px-1">
          Findings explain what changed once the hash no longer matches. Artifacts uploaded before
          structural baselines were recorded have no baseline to compare against.
        </p>
      </div>

      {/* FOOTER */}
      <div className="flex justify-between mt-3">
        <button onClick={back} className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium">
          <ArrowLeft size={14} /> Back
        </button>
        <button onClick={next} className="flex items-center gap-1.5 h-[44px] px-6 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition">
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}